import {
  useCanRead,
  useCanWrite,
  useHasFactoryAccess,
  type PermissionSection,
} from '@artic/api-client';
import { NoAccessState } from '@artic/ui/no-access-state';
import { Box, Flex, useToast } from '@chakra-ui/react';
import { useEffect, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { isTrunkUserCheck } from '../../atoms/isTrunkUserCheck';
import { ReadOnlyBanner } from '../../components/ReadOnlySection';

const ACCESS_DENIED_TOAST_ID = 'section-access-denied';

/**
 * A hub nav section. Factory is not a permission level: it is open to anyone
 * with access to at least one factory.
 */
export type HubSection = PermissionSection | 'factory';

/** Whether the user can read each hub section. */
export function useHubSectionAccess(): Record<HubSection, boolean> {
  return {
    factory: useHasFactoryAccess(),
    quality: useCanRead('quality'),
    planner: useCanRead('planner'),
    catalog: useCanRead('catalog'),
    materials: useCanRead('materials'),
    cost_to_produce: useCanRead('cost_to_produce'),
    settings: useCanRead('settings'),
    partner_api: useCanRead('partner_api'),
  };
}

/**
 * The hub's landing order when a user's permissions rule out their last
 * location: Factory, then Quality, then Planner. If none are readable the
 * user has nothing left in the hub: callers render the shared No access state
 * in place rather than looping them through hub routes they can't see either.
 *
 * `/quality/vision-quality-checks` is itself gated behind
 * `RequireFeatureFlag isTrunkUser` (AppRouter.tsx), so offering it to a
 * Quality-only partner user without Factory access bounced them to `/` and
 * back into `HubLandingRedirect` — a redirect loop. Only offer that rung to
 * a trunk user; everyone else falls through to Planner or out to Neo.
 */
export function firstReadableHubPath(
  canRead: {
    factory: boolean;
    quality: boolean;
    planner: boolean;
  },
  isTrunkUser: boolean,
): string | null {
  if (canRead.factory) return '/statusboard';
  if (canRead.quality && isTrunkUser) return '/quality/vision-quality-checks';
  if (canRead.planner) return '/workforce';
  return null;
}

/** The shared "No access" card, with the hub's copy for it. */
export function HubNoAccessState() {
  const { t } = useTranslation(['translation']);
  return (
    <NoAccessState
      title={t('permissions.no_access_title')}
      body={t('permissions.no_access_body')}
    />
  );
}

/**
 * Gates a route (or route subtree) on read access to a hub `section`.
 * Denied users see a one-time toast and are redirected to the first hub page
 * they can actually read — or shown the No access state in place when none
 * are. A reader without write access gets the section's read-only banner
 * once, above the page (Factory is access-only, so it never shows one).
 */
export default function RequireSection({
  children,
  section,
}: {
  children: JSX.Element;
  section: HubSection;
}) {
  const { t } = useTranslation(['translation']);
  const toast = useToast();
  const access = useHubSectionAccess();
  const isTrunkUser = useRecoilValue(isTrunkUserCheck);
  const canRead = access[section];

  const fallbackPath = canRead ? null : firstReadableHubPath(access, isTrunkUser);

  // Side effects stay out of render: the toast fires once per denial.
  useEffect(() => {
    if (canRead) return;
    if (!toast.isActive(ACCESS_DENIED_TOAST_ID)) {
      toast({
        id: ACCESS_DENIED_TOAST_ID,
        title: t('permissions.access_denied'),
        status: 'error',
        isClosable: true,
      });
    }
  }, [canRead, t, toast]);

  if (!canRead) {
    return fallbackPath ? <Navigate to={fallbackPath} replace /> : <HubNoAccessState />;
  }

  if (section === 'factory') return children;

  return <SectionReadOnlyFrame section={section}>{children}</SectionReadOnlyFrame>;
}

/**
 * The one read-only banner per hub page. Renders `children` untouched for a
 * writer; for a reader, stacks the banner above the page with the page body's
 * gutter so it lines up with the content below it.
 */
function SectionReadOnlyFrame({
  section,
  children,
}: {
  section: PermissionSection;
  children: JSX.Element;
}) {
  const canWrite = useCanWrite(section);
  if (canWrite) return children;

  return (
    <Flex direction="column" flex="1" minW={0} minH={0} h="full">
      <Box px={4} pt={4} flexShrink={0}>
        <ReadOnlyBanner section={section} />
      </Box>
      <Flex direction="column" flex="1" minW={0} minH={0}>
        {children}
      </Flex>
    </Flex>
  );
}
